/**
 * Skalerer et dokumentationsfoto i browseren, før det sendes til sagens mappe i SharePoint.
 *
 * Til forskel fra profilbilleder (512x512, se imageResize.ts) må disse ikke beskæres:
 * hele motivet skal med, og et tavleskilt eller et typenummer skal kunne læses bagefter.
 * 2048 px på den lange led rammer det: en typisk mobiloptagelse falder fra 4-8 MB til
 * 300-800 KB, uden at teksten på et relæ bliver ulæselig.
 *
 * Se docs/DECISIONS.md beslutning 023.
 */
const MAX_EDGE = 2048
const QUALITY = 0.8

/** Filtyper vi selv skalerer. Alt andet (PDF, tegninger) sendes uændret. */
const RESIZABLE = ['image/jpeg', 'image/png', 'image/webp']

export interface PreparedFile {
  blob: Blob
  fileName: string
}

/**
 * Gør en fil klar til upload. Billeder skaleres; alt andet sendes som det er.
 * Kan browseren ikke afkode billedet (f.eks. HEIC på en ældre Android), sendes
 * originalen videre - SharePoint kan selv vise den, og det er bedre end en fejl.
 */
export async function prepareDocumentFile(input: File, maxEdge = MAX_EDGE): Promise<PreparedFile> {
  if (!RESIZABLE.includes(input.type)) {
    return { blob: input, fileName: input.name }
  }

  try {
    const bitmap = await createImageBitmap(input)

    try {
      const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))

      // Allerede lille nok - så er der intet at vinde ved at kode den om.
      if (scale === 1 && input.size < 1_500_000) {
        return { blob: input, fileName: input.name }
      }

      const width = Math.round(bitmap.width * scale)
      const height = Math.round(bitmap.height * scale)

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const context = canvas.getContext('2d')
      if (!context) {
        return { blob: input, fileName: input.name }
      }

      // Hvid baggrund, så gennemsigtige PNG-billeder ikke bliver sorte som JPEG.
      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, width, height)
      context.drawImage(bitmap, 0, 0, width, height)

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', QUALITY)
      )

      if (!blob) {
        return { blob: input, fileName: input.name }
      }

      return { blob, fileName: toJpegName(input.name) }
    } finally {
      bitmap.close()
    }
  } catch {
    // Kunne ikke afkodes her - lad serveren og SharePoint tage den som den er.
    return { blob: input, fileName: input.name }
  }
}

/** Efter omkodning til JPEG skal endelsen passe til indholdet. */
function toJpegName(name: string) {
  const withoutExtension = name.replace(/\.[^.]+$/, '')
  return `${withoutExtension || 'foto'}.jpg`
}

/** "2,4 MB" - til at vise filstørrelser i galleriet. */
export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
}
